import { Helmet } from '@dr.pogodin/react-helmet'
import KeyIcon from '@mui/icons-material/Key'
import {
    Alert,
    Box,
    Button,
    Divider,
    Link,
    Stack,
    TextField,
    Typography
} from '@mui/material'
import { useEffect, useState } from 'react'
import {
    Link as RouterLink,
    useNavigate,
    useSearchParams
} from 'react-router-dom'
import AccountPageShell from '@/features/allauth/components/AccountPageShell'
import FormErrors from '@/features/allauth/components/FormErrors'
import * as allauth from '@/features/allauth/lib/allauth'
import {
    getCredential,
    isWebAuthnSupported,
    webAuthnErrors
} from '@/features/allauth/lib/webauthn'

const PROVIDER_LABELS = {
    microsoft: 'Microsoft',
    google: 'Google'
}

// Friendly messages for `?error=` codes the backend redirects to /login with.
const LOGIN_ERROR_MESSAGES = {
    partner_suspended:
        'Sign-in is currently unavailable for your organisation because its subscription is inactive. Please contact your administrator to restore access.'
}

const Login = () => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const loginErrorMessage = LOGIN_ERROR_MESSAGES[searchParams.get('error')]
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [discovery, setDiscovery] = useState(null)
    const [errors, setErrors] = useState([])
    const [isDiscovering, setIsDiscovering] = useState(false)
    const [isLoggingIn, setIsLoggingIn] = useState(false)
    const [isRedirecting, setIsRedirecting] = useState(false)
    const [passkeyLoginEnabled, setPasskeyLoginEnabled] = useState(false)
    const [isPasskeyLoggingIn, setIsPasskeyLoggingIn] = useState(false)

    // "Sign in with a passkey" shows only when allauth has passkey login on
    // (MFA_PASSKEY_LOGIN_ENABLED) and this browser can use one.
    useEffect(() => {
        if (!isWebAuthnSupported()) return undefined
        let cancelled = false
        allauth
            .getConfig()
            .then((result) => {
                if (!cancelled) {
                    setPasskeyLoginEnabled(
                        Boolean(result?.data?.mfa?.passkey_login_enabled)
                    )
                }
            })
            .catch(() => null)
        return () => {
            cancelled = true
        }
    }, [])

    // Passkey sign-in: allauth's request options name no account, so the
    // browser offers every passkey it holds for this site; the one picked
    // tells allauth who is signing in. It is a complete sign-in on its own
    // (allauth records it as two-factor), so there is no code step after it.
    const handlePasskeyLogin = async () => {
        try {
            setIsPasskeyLoggingIn(true)
            setErrors([])
            const options = await allauth.getWebAuthnLoginOptions()
            const credential = await getCredential(
                options?.data?.request_options
            )
            const result = await allauth.loginWithWebAuthn(credential)
            if (allauth.followPendingFlow(result, navigate)) return
            if (result?.meta?.is_authenticated) {
                navigate('/')
                return
            }
            setErrors([
                {
                    message:
                        'We could not sign you in with that passkey. Enter your email address to sign in another way.'
                }
            ])
        } catch (err) {
            setErrors(webAuthnErrors(err))
        } finally {
            setIsPasskeyLoggingIn(false)
        }
    }

    const handleDiscover = async (event) => {
        event.preventDefault()
        try {
            setIsDiscovering(true)
            setErrors([])
            const result = await allauth.discoverTenantLogin({ email })
            setDiscovery(result)
            setPassword('')
        } catch (err) {
            setErrors(
                err.response?.data?.errors || [
                    { message: 'Unable to continue.' }
                ]
            )
        } finally {
            setIsDiscovering(false)
        }
    }

    const handlePasswordLogin = async (event) => {
        event.preventDefault()
        try {
            setIsLoggingIn(true)
            setErrors([])
            const result = await allauth.login({ email, password })
            const pendingFlow = allauth.getPendingFlow(result)
            if (pendingFlow) {
                const url = allauth.pendingFlowUrl(pendingFlow)
                if (url) {
                    navigate(url)
                    return
                }
            }
            navigate('/')
        } catch (err) {
            setErrors(
                err.response?.data?.errors || [{ message: 'Unable to log in.' }]
            )
        } finally {
            setIsLoggingIn(false)
        }
    }

    const handleTenantRedirect = async () => {
        if (!discovery?.discovery_token) {
            return
        }

        try {
            setIsRedirecting(true)
            setErrors([])
            await allauth.redirectToTenantProvider(discovery.discovery_token)
        } catch (err) {
            setErrors(
                err.response?.data?.errors || [
                    { message: 'Unable to start single sign-on.' }
                ]
            )
            setIsRedirecting(false)
        }
    }

    const tenantSsoProviderLabel = PROVIDER_LABELS[discovery?.provider] || 'SSO'

    return (
        <>
            <Helmet>
                <title>Login</title>
            </Helmet>
            <AccountPageShell>
                <Typography variant="h4" gutterBottom>
                    Login
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Enter your email and we&apos;ll take you to the right
                    sign-in for your organisation.
                </Typography>

                {loginErrorMessage ? (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        {loginErrorMessage}
                    </Alert>
                ) : null}

                <FormErrors errors={errors} />

                {!discovery ? (
                    <Box component="form" noValidate onSubmit={handleDiscover}>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Email Address"
                            margin="normal"
                            onChange={(event) => setEmail(event.target.value)}
                            autoComplete="username"
                            name="email"
                            type="email"
                            value={email}
                            variant="outlined"
                        />
                        <Box sx={{ mt: 2 }}>
                            <Button
                                color="primary"
                                disabled={isDiscovering || !email}
                                fullWidth
                                size="large"
                                type="submit"
                                variant="contained"
                            >
                                Continue
                            </Button>
                        </Box>
                        {passkeyLoginEnabled ? (
                            <>
                                <Divider sx={{ my: 2 }}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        or
                                    </Typography>
                                </Divider>
                                <Button
                                    disabled={isPasskeyLoggingIn}
                                    fullWidth
                                    size="large"
                                    startIcon={<KeyIcon />}
                                    variant="outlined"
                                    onClick={handlePasskeyLogin}
                                >
                                    Sign in with a passkey
                                </Button>
                            </>
                        ) : null}
                    </Box>
                ) : (
                    <Stack spacing={2} sx={{ mt: 3 }}>
                        {/*<Alert severity="info" variant="outlined">*/}
                        {/*    Using <strong>{discovery.email}</strong>.*/}
                        {/*</Alert>*/}

                        {discovery.tenant_sso_configured ? (
                            <Button
                                disabled={isRedirecting}
                                fullWidth
                                size="large"
                                variant="outlined"
                                onClick={handleTenantRedirect}
                            >
                                Continue with {tenantSsoProviderLabel}
                            </Button>
                        ) : null}

                        {/* Nothing is announced when the answer is simply
                            "sign in with your password" — the password field is
                            right there. An alert is only worth the reader's
                            attention when they are actually stuck, which is the
                            case below: no SSO and no password login either. */}
                        {!discovery.tenant_sso_configured &&
                        !discovery.password_login_allowed ? (
                            <Alert severity="warning" variant="outlined">
                                Your organisation hasn&apos;t finished setting
                                up sign-in for this address. Ask your IT
                                administrator to enable access for{' '}
                                {discovery.email}.
                            </Alert>
                        ) : null}

                        {discovery.password_login_allowed &&
                        !discovery.tenant_sso_configured ? (
                            <Box
                                component="form"
                                noValidate
                                onSubmit={handlePasswordLogin}
                            >
                                {/* The email was entered on the previous step,
                                    in another form. Password managers pair a
                                    password with the username field in the
                                    SAME form, so repeat it here, hidden. */}
                                <input
                                    type="email"
                                    name="username"
                                    autoComplete="username"
                                    value={discovery.email ?? email}
                                    readOnly
                                    hidden
                                />
                                <TextField
                                    autoFocus
                                    fullWidth
                                    label="Password"
                                    margin="normal"
                                    name="password"
                                    autoComplete="current-password"
                                    onChange={(event) =>
                                        setPassword(event.target.value)
                                    }
                                    type="password"
                                    value={password}
                                    variant="outlined"
                                />
                                <Button
                                    color="primary"
                                    disabled={isLoggingIn || !password}
                                    fullWidth
                                    size="large"
                                    type="submit"
                                    variant="contained"
                                >
                                    Login with password
                                </Button>
                                {/* Only on the password step, which an SSO user
                                    never reaches — their password lives with
                                    their identity provider, not here. Carries the
                                    address so they don't retype it. */}
                                <Box sx={{ mt: 1.5, textAlign: 'right' }}>
                                    <Link
                                        component={RouterLink}
                                        to={`/password-recovery?email=${encodeURIComponent(discovery.email || email)}`}
                                        variant="body2"
                                    >
                                        Forgot password?
                                    </Link>
                                </Box>
                            </Box>
                        ) : null}

                        <Button
                            color="inherit"
                            onClick={() => {
                                setDiscovery(null)
                                setErrors([])
                                setPassword('')
                            }}
                        >
                            Use a different email
                        </Button>
                    </Stack>
                )}

                {/*<Box*/}
                {/*    sx={{*/}
                {/*        mt: 2,*/}
                {/*        display: 'flex',*/}
                {/*        justifyContent: 'space-between'*/}
                {/*    }}*/}
                {/*>*/}
                {/*<Link*/}
                {/*    component={RouterLink}*/}
                {/*    to="/password-recovery"*/}
                {/*    variant="body2"*/}
                {/*>*/}
                {/*    Forgot password?*/}
                {/*</Link>*/}
                {/*<Link component={RouterLink} to="/register" variant="body2">*/}
                {/*    Create an account*/}
                {/*</Link>*/}
                {/*</Box>*/}
            </AccountPageShell>
        </>
    )
}

export default Login
